import { Component } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TurbineService } from '../../services/turbine.service';

@Component({
    selector: 'app-turbine-bulk-upload',
    templateUrl: './turbine-bulk-upload.component.html',
    styleUrls: ['./turbine-bulk-upload.component.css'],
    standalone: false
})
export class TurbineBulkUploadComponent {
  selectedFile: File | null = null;
  uploading = false;
  uploadProgress = 0;
  fileName = '';

  constructor(
    public dialogRef: MatDialogRef<TurbineBulkUploadComponent>,
    private turbineService: TurbineService,
    private snackBar: MatSnackBar
  ) {}

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      // Validate file extension
      const validExtensions = ['.xls', '.xlsx'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.'));
      
      if (validExtensions.includes(fileExtension.toLowerCase())) {
        this.selectedFile = file;
        this.fileName = file.name;
      } else {
        this.snackBar.open(
          'Invalid File Format. Please select an excel file to upload',
          'Close',
          { duration: 5000, panelClass: ['error-snackbar'] }
        );
        this.selectedFile = null;
        this.fileName = '';
      }
    }
  }

  uploadFile(): void {
    if (!this.selectedFile) {
      this.snackBar.open(
        'Please select an excel file to upload',
        'Close',
        { duration: 3000, panelClass: ['warning-snackbar'] }
      );
      return;
    }

    this.uploading = true;
    this.uploadProgress = 0;
    
    this.turbineService.bulkUploadTurbines(this.selectedFile)
      .subscribe({
        next: (response) => {
          this.uploading = false;
          
          if (response.success) {
            this.snackBar.open(
              response.message || 'Turbine Data Uploaded Successfully',
              'Close',
              { duration: 5000, panelClass: ['success-snackbar'] }
            );
            this.dialogRef.close(true); // Close dialog and indicate success
          } else {
            this.snackBar.open(
              `Error: ${response.message}`,
              'Close',
              { duration: 7000, panelClass: ['error-snackbar'] }
            );
          }
        },
        error: (error) => {
          this.uploading = false;
          const errorMessage = error.error?.message 
            || error.message 
            || 'Upload failed. Please try again.';
          
          this.snackBar.open(
            `Error: ${errorMessage}`,
            'Close',
            { duration: 7000, panelClass: ['error-snackbar'] }
          );
        }
      });
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  clearFile(): void {
    this.selectedFile = null;
    this.fileName = '';
  }

  downloadTemplate(): void {
    // Define all 34 column headers matching the EXCELTURBINESTG table
    const headers = [
      'PLANT',
      'SERIAL NUMBER',
      'UNIT SHORT NAME',
      'COUNTRY',
      'EQUIPMENT CODE',
      'OEM CRITICALITY RANKING',
      'CVX CRITICALITY RANKING',
      'FIRST FIRE DATE',
      'CAPACITY',
      'RPM',
      'CYCLE HZ',
      'PRIMARY FUEL',
      'ALT FUEL1',
      'ALT FUEL2',
      'COMBUSTION SYSTEM',
      'DRIVEN EQUIPMENT',
      'DRIVEN EQUIPMENT OEM',
      'OEM SUPPORT RELATIONSHIP',
      'INSTALLATION TYPE',
      'SERVICE TYPE',
      'PSC MSC MONITORED',
      'OEM MONITORED',
      'LOCAL DSC MONITORED',
      'IMPACT MANAGED',
      'ORAP REPORTED',
      'CHEVRON UNIT NAME',
      'DESIGN HEATRATE',
      'END SERVICE DATE',
      'GT OEM',
      'GT CONTROL SYSTEM',
      'INSTALL YEAR',
      'TURBINE ID',
      'FRAME TYPE',
      'Offshore'
    ];

    // Create CSV content (Excel can open CSV files)
    const csvContent = headers.join(',') + '\n';
    
    // Create and download as Excel file
    const blob = new Blob([csvContent], { 
      type: 'application/vnd.ms-excel' 
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Turbine_Bulk_Upload_Template.csv';
    link.click();
    window.URL.revokeObjectURL(url);
    
    this.snackBar.open(
      'Template downloaded successfully. Fill it in Excel and save as .xlsx before uploading.',
      'Close',
      { duration: 5000, panelClass: ['success-snackbar'] }
    );
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
